import React, { useEffect, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import { NewsletterDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import { useNewsletterCategories } from '../../hooks/useNewsletterCategoryApi';
import { slugify } from '../../utils/slugify';

interface NewsletterFormProps {
  initialValues: NewsletterDTO;
  onSubmit: (data: NewsletterDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
} as const;

const NewsletterForm: React.FC<NewsletterFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<NewsletterDTO>(initialValues);

  const validationRules: ValidationRules = {
    title: [{ required: true, message: 'Title is required' }],
    volume: [{ required: true, message: 'Volume is required' }],
    date: [
      { required: true, message: 'Date is required' },
      {
        custom: (value) => !isNaN(Date.parse(value)),
        message: 'Date must be a valid date',
      },
    ],
  };

  const { errors, validate } =
    useFormValidation<NewsletterDTO>(validationRules);

  const { data: newsletterCategoriesData } = useNewsletterCategories();

  // Slug is derived from the Title and shown read-only — the admin only types a Title,
  // mirroring the Pages form. Kept in sync whenever the Title changes.
  useEffect(() => {
    setFormData((prev) => ({ ...prev, slug: slugify(prev.title || '') }));
  }, [formData.title]);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit({ ...formData, slug: slugify(formData.title || '') });
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isLoading}
    >
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required={true}
          error={errors.title}
        />
        <FormField
          id="slug"
          label="Title URL"
          type="text"
          value={formData.slug || ''}
          onChange={() => {}}
          placeholder="Auto-generated from the title"
          required={false}
          disabled={true}
          helper="The public URL for this newsletter. Auto-generated from the title."
        />
        <FormField
          id="year"
          label="Year"
          type="number"
          value={formData.year || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Year"
          required={false}
          error={errors.year}
        />
        <FormField
          id="volume"
          label="Volume"
          type="text"
          value={formData.volume || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Volume"
          required={true}
          error={errors.volume}
        />
        <FormField
          id="date"
          label="Date"
          type="date"
          value={formData.date || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Date"
          required={true}
          error={errors.date}
        />
        <FormField
          id="newsletterCategoryId"
          label="Category"
          type="dropdown"
          value={formData.newsletterCategoryId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Category"
          required={false}
          error={errors.newsletterCategoryId}
          options={(newsletterCategoriesData || []).map((nc) => ({
            value: nc.id || '',
            label: nc.title || '',
          }))}
        />
        <FormField
          id="content"
          label="Content"
          type="richtext"
          value={formData.content || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Content"
          required={false}
          error={errors.content}
        />
        <FormField
          id="isActive"
          label="Active"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          required={false}
          error={errors.isActive}
          helper="Check to make record active"
        />
      </FormSection>
    </Form>
  );
};

export default NewsletterForm;
